/**
 * Centralized service for fetching and caching Pillar embed URL
 * Ensures only one API call is made regardless of how many components need the URL
 */

// Shared cache for Pillar URL to avoid multiple API calls
let cachedPillarUrl: string | null = null;
let pillarUrlPromise: Promise<string> | null = null;

/**
 * Fetches the Pillar embed URL
 * Uses a shared cache to prevent duplicate API calls
 * Safe to call from multiple components simultaneously
 *
 * @returns Promise that resolves to the Pillar embed URL
 */
export async function getPillarURL(): Promise<string> {
    // Return cached URL if available
    if (cachedPillarUrl) {
        return cachedPillarUrl;
    }

    // If a request is already in progress, wait for it
    if (pillarUrlPromise) {
        return pillarUrlPromise;
    }

    // Start new request and cache the promise
    pillarUrlPromise = (async () => {
        try {
            const response = await fetch('/pillar_embed');
            if (response.ok) {
                const url = await response.text();
                cachedPillarUrl = url;
                return url;
            } else {
                console.error('[PILLAR-URL] Failed to fetch:', response.statusText);
                throw new Error('Failed to fetch the Pillar URL');
            }
        } finally {
            // Clear the promise after completion (success or failure)
            pillarUrlPromise = null;
        }
    })();

    return pillarUrlPromise;
}

/**
 * Clears the cached Pillar URL
 * Useful for testing or if the URL needs to be refreshed
 */
export function clearPillarURLCache(): void {
    cachedPillarUrl = null;
    pillarUrlPromise = null;
}
